import { Link } from "react-router-dom";
import {
  MessageCircle,
  ShieldCheck,
  Sparkles,
  CalendarCheck,
  Users,
  Building2,
  Star,
  Globe2,
  Rocket,
  HeartHandshake,
  Quote,
  ArrowRight,
} from "lucide-react";
import Button from "../components/ui/Button";
import { Card, Badge } from "../components/ui/Card";

const STATS = [
  { value: "10K+", label: "Bookings confirmed" },
  { value: "500+", label: "Businesses listed" },
  { value: "98%", label: "Show-up rate" },
  { value: "24/7", label: "AI assistant uptime" },
];

const VALUES = [
  {
    icon: MessageCircle,
    title: "WhatsApp-first",
    description:
      "Clients confirm, reschedule, and get reminders where they already are — no app download required to stay in the loop.",
  },
  {
    icon: ShieldCheck,
    title: "Trust by default",
    description:
      "Secure mobile money payments, verified businesses, and clear cancellation policies keep every booking safe on both sides.",
  },
  {
    icon: Sparkles,
    title: "AI that saves time",
    description:
      "Our scheduling assistant fills gaps, handles reminders, and answers common questions so no-shows and back-and-forth drop away.",
  },
  {
    icon: HeartHandshake,
    title: "Built for real businesses",
    description:
      "Every feature starts with a conversation with a salon owner, barber, or clinic — not a spec sheet. We ship what people actually ask for.",
  },
];

const STEPS = [
  {
    icon: Users,
    title: "Discover",
    description: "Search salons, barbers, clinics, tutors, and more near you, with real reviews and live availability.",
  },
  {
    icon: CalendarCheck,
    title: "Book & pay",
    description: "Pick a slot, pay securely with mobile money, and get an instant WhatsApp confirmation — no phone tag.",
  },
  {
    icon: Building2,
    title: "Grow",
    description: "Businesses manage staff, services, and payments from one dashboard built for the way they actually work.",
  },
];

const TIMELINE = [
  {
    year: "2024",
    title: "The problem, up close",
    description:
      "We watched salon owners juggle three notebooks, a WhatsApp inbox, and a no-show rate nobody could explain. SHUWA started as a fix for that one problem.",
  },
  {
    year: "2025",
    title: "First 500 businesses",
    description:
      "Salons, barbers, clinics, and tutors across the continent came onboard — and told us exactly what to build next.",
  },
  {
    year: "Now",
    title: "Booking that actually sticks",
    description:
      "WhatsApp reminders, secure payments, and an AI assistant that fills gaps automatically — built from hundreds of real conversations.",
  },
];

export default function About() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-secondary/10 to-white px-4 py-16 sm:px-6 sm:py-24">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-primary/20 blur-[120px]"
        />
        <div className="relative mx-auto max-w-3xl text-center">
          <Badge variant="primary">About SHUWA</Badge>
          <h1 className="mt-4 font-montserrat text-4xl font-bold text-slate-900 sm:text-5xl">
            Booking, made effortless for Africa's service businesses
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            SHUWA is a booking and business management platform built for the African service market —
            salons, barbers, clinics, tutors, and more. We combine WhatsApp-first communication, secure
            mobile money payments, and an AI scheduling assistant so every appointment is confirmed and assured.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button as={Link} to="/for-business" size="lg">
              List your business
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button as={Link} to="/search" variant="outline" size="lg">
              Find a business
            </Button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="px-4 py-10 sm:px-6">
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-6 sm:grid-cols-4">
          {STATS.map((stat) => (
            <Card key={stat.label} className="px-4 py-6 text-center">
              <div className="font-montserrat text-2xl font-bold text-slate-900 sm:text-3xl">{stat.value}</div>
              <div className="mt-1 text-xs text-slate-500 sm:text-sm">{stat.label}</div>
            </Card>
          ))}
        </div>
      </section>

      {/* Mission / story */}
      <section className="px-4 py-14 sm:px-6">
        <div className="mx-auto grid max-w-5xl items-center gap-10 sm:grid-cols-2">
          <div>
            <Badge variant="primary">Our mission</Badge>
            <h2 className="mt-4 font-montserrat text-2xl font-bold text-slate-900 sm:text-3xl">
              Every business deserves a full calendar
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">
              Too many great businesses lose bookings to missed calls, forgotten appointments, and messy
              WhatsApp threads. SHUWA exists to close that gap — giving every salon, barber, clinic, and
              tutor the same booking infrastructure that big brands take for granted, without the
              complexity or the price tag.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">
              We're building for the way people in Africa's service economy actually work: on WhatsApp, on
              mobile, and on trust. That means fewer taps, faster payouts, and reminders that reach clients
              where they already are.
            </p>
          </div>
          <div className="relative">
            <div
              aria-hidden
              className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-br from-primary/20 to-secondary/20 blur-2xl"
            />
            <Card className="space-y-5 p-7 sm:p-8">
              <Globe2 className="h-9 w-9 text-primary" />
              <div>
                <p className="font-montserrat text-lg font-semibold text-slate-900">Built in Africa, for Africa</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  From Kigali to Lagos to Nairobi, SHUWA is designed around local payment rails, local
                  languages, and the way clients here actually book — starting with a chat, not a form.
                </p>
              </div>
              <div className="flex items-center gap-2 border-t border-border pt-5">
                <Rocket className="h-4 w-4 text-primary" />
                <p className="text-sm font-medium text-slate-700">Growing to every service business on the continent</p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-slate-50 px-4 py-14 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-montserrat text-2xl font-bold text-slate-900 sm:text-3xl">Why businesses and clients choose us</h2>
            <p className="mt-3 text-slate-600">
              We built SHUWA around the habits people already have — WhatsApp and mobile money
              payments, and a need for appointments that actually stick.
            </p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map(({ icon: Icon, title, description }) => (
              <Card key={title} className="p-6 transition-shadow hover:shadow-md">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-montserrat text-lg font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-4 py-14 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-montserrat text-2xl font-bold text-slate-900 sm:text-3xl">How SHUWA works</h2>
            <p className="mt-3 text-slate-600">Three simple steps, whether you're booking a service or running one.</p>
          </div>
          <div className="mt-10 grid gap-8 sm:grid-cols-3">
            {STEPS.map(({ icon: Icon, title, description }, i) => (
              <div key={title} className="relative">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white">
                    {i + 1}
                  </div>
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="mt-4 font-montserrat text-lg font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-slate-50 px-4 py-14 sm:px-6">
        <div className="mx-auto max-w-3xl">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-montserrat text-2xl font-bold text-slate-900 sm:text-3xl">Our journey so far</h2>
            <p className="mt-3 text-slate-600">Still early — and building fast, with our businesses in the room.</p>
          </div>
          <div className="mt-10 space-y-8 border-l border-border pl-6 sm:pl-8">
            {TIMELINE.map((item) => (
              <div key={item.title} className="relative">
                <span className="absolute -left-[31px] top-1 h-3 w-3 rounded-full border-2 border-white bg-primary sm:-left-[39px]" />
                <span className="text-xs font-semibold uppercase tracking-wide text-primary">{item.year}</span>
                <h3 className="mt-1 font-montserrat text-lg font-semibold text-slate-900">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6 sm:py-20">
        <Card className="relative overflow-hidden p-8 text-center sm:p-12">
          <Quote className="mx-auto h-8 w-8 text-primary/30" />
          <div className="mt-4 flex justify-center gap-1 text-amber-400">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-current" />
            ))}
          </div>
          <p className="mt-5 font-montserrat text-lg font-medium text-slate-900 sm:text-xl">
            "SHUWA cut our no-shows in half. Clients get a WhatsApp reminder automatically and I
            haven't touched a paper booking book in months."
          </p>
          <p className="mt-4 text-sm font-semibold text-slate-900">Amara K.</p>
          <p className="text-sm text-muted-foreground">Owner, Amara's Hair Studio</p>
        </Card>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-slate-950 px-4 py-16 text-center sm:px-6 sm:py-24">
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-primary/30 blur-[110px]"
        />
        <div className="relative mx-auto max-w-2xl">
          <h2 className="font-montserrat text-2xl font-bold text-white sm:text-3xl">
            Ready to bring your business onto SHUWA?
          </h2>
          <p className="mt-3 text-slate-400">
            Join hundreds of businesses managing bookings, staff, and payments in one place.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button as={Link} to="/for-business" size="lg">
              List your business
            </Button>
            <Button as={Link} to="/contact" variant="outline" size="lg" className="border-white/20 text-white hover:bg-white/10">
              Talk to us
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
